/**
 * 检测数据类型方法
 *
 * @date 2022-07-29
 */

/** 判断是否为纯对象（由 Object 构造，而非 Array、Date 等） */
export const isObject = (data: unknown): data is Record<string, unknown> => {
  return Object.prototype.toString.call(data) === '[object Object]';
};

/** 判断是否为 null 或 undefined */
export const isNull = (data: unknown): boolean => {
  return data === null || data === undefined;
};

/** 判断是否为空值：null、undefined、空数组、空对象、空字符串（仅空白也算空） */
export const isEmpty = (data: unknown): boolean => {
  if (data === null || data === undefined) return true;
  if (Object.prototype.toString.call(data) === '[object Array]') {
    return (data as unknown[]).length === 0;
  }

  if (Object.prototype.toString.call(data) === '[object Object]') {
    return Object.keys(data as object).length === 0;
  }

  if (typeof data === 'string') return data.trim() === '';
  return false;
};

export function isEmptyObj(obj: unknown): boolean {
  if (obj == null) {
    return true;
  }
  return obj && typeof obj === 'object' && Object.keys(obj).length === 0;
}

/**
 * 深拷贝对象。仅处理对象自身可枚举属性；数组、Set、Map 做一层拷贝，其内部对象不再深拷贝。
 *
 * @param data 待拷贝的普通对象（不可为 null）
 * @returns 拷贝后的新对象
 * @throws {TypeError} 当入参不是对象时
 */
export function deepCopy(data: Record<string, unknown>): Record<string, unknown> {
  if (typeof data !== 'object' || data === null) {
    throw new TypeError('传入参数不是对象');
  }
  const newData: Record<string, unknown> = {};
  const dataKeys = Object.keys(data);
  dataKeys.forEach((key) => {
    const currentDataValue = data[key];
    if (typeof currentDataValue !== 'object' || currentDataValue === null) {
      newData[key] = currentDataValue;
    } else if (Array.isArray(currentDataValue)) {
      newData[key] = [...currentDataValue];
    } else if (currentDataValue instanceof Set) {
      newData[key] = new Set([...currentDataValue]);
    } else if (currentDataValue instanceof Map) {
      newData[key] = new Map([...currentDataValue]);
    } else {
      newData[key] = deepCopy(currentDataValue as Record<string, unknown>);
    }
  });
  return newData;
}

/**
 * 深拷贝对象或数组。与 deepCopy 不同，会对数组元素、对象属性中的嵌套对象/数组递归深拷贝。
 * 支持：基本类型、函数、普通对象、数组、Set（一层拷贝）、Map（一层拷贝）。
 *
 * @param data 待拷贝的普通对象或数组（不可为 null）
 * @returns 拷贝后的新对象或新数组
 * @throws {TypeError} 当入参不是对象时
 */
export function deepCopyNew(
  data: Record<string, unknown> | unknown[],
): Record<string, unknown> | unknown[] {
  if (typeof data !== 'object' || data === null) {
    throw new TypeError('传入参数不是对象');
  }

  // 数组：创建新数组，对每个元素递归深拷贝（对象/数组走 deepCopy，其余直接复用）
  if (Array.isArray(data)) {
    return data.map((item: unknown): unknown => {
      if (typeof item === 'object' && item !== null) {
        return deepCopy(item as Record<string, unknown>);
      }
      return item;
    });
  }

  // 普通对象：创建新对象，对每个属性按类型分别处理
  const newData: Record<string, unknown> = {};
  const dataKeys = Object.keys(data);
  dataKeys.forEach((key: string) => {
    const currentDataValue = data[key];
    // 基本类型、函数：直接赋值
    if (typeof currentDataValue !== 'object' || currentDataValue === null) {
      newData[key] = currentDataValue;
    } else if (Array.isArray(currentDataValue)) {
      // 数组：逐项深拷贝（对象/数组走 deepCopy，其余直接复用）
      newData[key] = currentDataValue.map((item: unknown): unknown => {
        if (typeof item === 'object' && item !== null) {
          return deepCopy(item as Record<string, unknown>);
        }
        return item;
      });
    } else if (currentDataValue instanceof Set) {
      // Set：展开后新建 Set（一层拷贝）
      newData[key] = new Set([...currentDataValue]);
    } else if (currentDataValue instanceof Map) {
      // Map：展开后新建 Map（一层拷贝）
      newData[key] = new Map([...currentDataValue]);
    } else {
      // 普通对象：递归深拷贝
      newData[key] = deepCopy(currentDataValue as Record<string, unknown>);
    }
  });
  return newData;
}

/** 判断是否为字符串（含 new String() 创建的包装对象） */
export const isString = (data: unknown): boolean => {
  return Object.prototype.toString.call(data) === '[object String]';
};

/** 判断是否为数组 */
export const isArray = (data: unknown): data is unknown[] => {
  return Object.prototype.toString.call(data) === '[object Array]';
};

/** 返回与 typeof 类似的类型名（小写），如 'object'、'array'、'string' */
export const typeOf = (data: unknown): string => {
  return Object.prototype.toString
    .call(data)
    .replace(/\[object (\w+)\]/, '$1')
    .toLowerCase();
};
